import { useState } from 'react';
import { useForm } from 'react-hook-form';
import api from '../../api/client';
import { changePassword } from '../../api/auth';
import { useNotification } from '../../context/NotificationContext';
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Loader2, Lock, CheckCircle2, XCircle, X, Eye, EyeOff } from "lucide-react";
import { useNavigate } from 'react-router-dom';

export default function ChangePassword() {
 const { register, handleSubmit, reset, watch, formState: { errors } } = useForm();
 const [showCurrentPassword, setShowCurrentPassword] = useState(false);
 const [showNewPassword, setShowNewPassword] = useState(false);
 const [showConfirmPassword, setShowConfirmPassword] = useState(false);
 const [loading, setLoading] = useState(false);
 const [message, setMessage] = useState("");
 const [error, setError] = useState("");
 const { add } = useNotification();
 const navigate = useNavigate();

 const onSubmit = async (data) => {
 if (data.newPassword !== data.confirmPassword) {
 setError("New passwords do not match");
 return;
 }

 setLoading(true);
 setError("");
 setMessage("");

 try {
 await changePassword({
 currentPassword: data.currentPassword,
 newPassword: data.newPassword
 });
 setMessage("Password changed successfully!");
 reset();
 add("Password changed successfully", "success");
 
 // Redirect to profile page after 2 seconds
 setTimeout(() => {
 navigate('/profile');
 }, 2000);
 } catch (err) {
 console.error(err);
 setError(err.response?.data?.message || "Failed to change password");
 add(err.response?.data?.message || "Failed to change password", "error");
 } finally {
 setLoading(false);
 }
 };

 const handleClose = () => {
 navigate('/profile');
 };

 return (
 <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 relative">
 <button
 onClick={handleClose}
 className="absolute top-4 right-4 p-2 rounded-full hover:bg-muted/70 dark:hover:bg-accent transition-colors"
 aria-label="Close"
 >
 <X className="w-6 h-6 text-muted-foreground dark:text-muted-foreground/80" />
 </button>
 
 <div className="max-w-md mx-auto">
 <Card className="shadow-lg border border-gray-100 dark:border-gray-800">
 <CardHeader>
 <CardTitle className="text-2xl font-bold text-center flex items-center justify-center gap-2">
 <Lock className="w-6 h-6 text-primary" />
 Change Password
 </CardTitle>
 </CardHeader>
 <CardContent>
 {message && (
 <Alert className="mb-4 border-green-200 bg-green-500/10 ">
 <CheckCircle2 className="h-4 w-4 text-green-600" />
 <AlertDescription className="text-green-800 dark:text-green-200">
 {message} - Redirecting to profile...
 </AlertDescription>
 </Alert>
 )}
 {error && (
 <Alert variant="destructive" className="mb-4">
 <XCircle className="h-4 w-4" />
 <AlertDescription>{error}</AlertDescription>
 </Alert>
 )}

 <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
 <div className="space-y-2">
 <label className="text-sm font-medium text-foreground/90 dark:text-gray-300">Current Password</label>
 <div className="relative">
 <Input
 type={showCurrentPassword ? "text" : "password"}
 {...register("currentPassword", { required: "Current password is required" })}
 className="pr-10"
 />
 <button
 type="button"
 onClick={() => setShowCurrentPassword(!showCurrentPassword)}
 className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground/90 focus:outline-none"
 tabIndex={-1}
 >
 {showCurrentPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
 </button>
 </div>
 {errors.currentPassword && <p className="text-sm text-red-500">{errors.currentPassword.message}</p>}
 </div>

 <div className="space-y-2">
 <label className="text-sm font-medium text-foreground/90 dark:text-gray-300">New Password</label>
 <div className="relative">
 <Input
 type={showNewPassword ? "text" : "password"}
 {...register("newPassword", { 
 required: "New password is required",
 minLength: { value: 6, message: "Password must be at least 6 characters" }
 })}
 className="pr-10"
 />
 <button
 type="button"
 onClick={() => setShowNewPassword(!showNewPassword)}
 className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground/90 focus:outline-none"
 tabIndex={-1}
 >
 {showNewPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
 </button>
 </div>
 {errors.newPassword && <p className="text-sm text-red-500">{errors.newPassword.message}</p>}
 </div>

 <div className="space-y-2">
 <label className="text-sm font-medium text-foreground/90 dark:text-gray-300">Confirm New Password</label>
 <div className="relative">
 <Input
 type={showConfirmPassword ? "text" : "password"}
 {...register("confirmPassword", { required: "Please confirm your password" })}
 className="pr-10"
 />
 <button
 type="button"
 onClick={() => setShowConfirmPassword(!showConfirmPassword)}
 className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground/90 focus:outline-none"
 tabIndex={-1}
 >
 {showConfirmPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
 </button>
 </div>
 {errors.confirmPassword && <p className="text-sm text-red-500">{errors.confirmPassword.message}</p>}
 </div>

 <Button 
 type="submit" 
 className="w-full bg-primary hover:bg-primary/90 text-white"
 disabled={loading}
 >
 {loading ? (
 <>
 <Loader2 className="mr-2 h-4 w-4 animate-spin" />
 Updating...
 </>
 ) : (
 "Update Password"
 )}
 </Button>
 </form>
 </CardContent>
 </Card>
 </div>
 </div>
 );
}